import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react'
import classNames from 'classnames'
import React, { useState } from 'react'

import { optimizeMessagingAttachmentUrl } from '../../../../../utils/cdnImageUrl'
import { AttachmentThumbnail, getSourceType } from '../../../../AttachmentCard'
import type { LockedAttachmentGalleryItem, PaymentStatus } from '../../types'

import { LockBadge } from './LockBadge'
import type { CardVariant } from './MediaShell'

export interface GalleryThumbnailProps {
  variant: CardVariant
  gallery: LockedAttachmentGalleryItem[]
  title?: string
  /** When true, all items are shown blurred + the locked/play badge is overlaid. */
  showLocked: boolean
  paymentStatus?: PaymentStatus
  /** Top-right slot (e.g. dismiss X on Composer). Rendered inside the thumbnail. */
  topRight?: React.ReactNode
}

function isPlayableItem(item: LockedAttachmentGalleryItem): boolean {
  const sourceType = getSourceType(item.mimeType)
  return sourceType === 'video' || sourceType === 'audio'
}

/**
 * Mixed-media carousel used by every LockedAttachment card when its `gallery`
 * prop has 2+ items. Matches the "Gallery" frames in the Figma design system:
 * 337/386 aspect ratio, prev/next caret buttons, a counter pill, and a center
 * lock / play indicator that swaps based on the current item.
 */
const GalleryThumbnail: React.FC<GalleryThumbnailProps> = ({
  variant,
  gallery,
  title,
  showLocked,
  paymentStatus,
  topRight,
}) => {
  const [index, setIndex] = useState(0)
  const total = gallery.length
  const safeIndex = Math.min(index, total - 1)
  const current = gallery[safeIndex]
  const isVideoItem = isPlayableItem(current)
  const optimizedCurrentThumbnailUrl = optimizeMessagingAttachmentUrl(
    current.thumbnailUrl
  )

  // Stop propagation so arrow taps don't bubble up to the outer preview-toggle
  // wrapper on PreviewMediaCard / OwnMediaCard.
  const goPrev = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIndex((i) => Math.max(0, i - 1))
  }
  const goNext = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIndex((i) => Math.min(total - 1, i + 1))
  }
  const atStart = safeIndex === 0
  const atEnd = safeIndex === total - 1

  const isDark = variant === 'dark'

  // Locked + unlocked render entirely separate trees so the locked state
  // doesn't end up stacking a blurred `<img>` on top of an underlying
  // `AttachmentThumbnail` (whose `bg-white/10` poster shell otherwise
  // shows through as a visible horizontal seam).
  //
  // The locked placeholder is absolutely positioned, so it needs the fixed
  // 337/386 box for height; unlocked content (video, natural-sized image)
  // manages its own height and can expand/shrink per item.
  //
  // The locked branch uses `filter: blur` on a single full-size `<img>`
  // — not `backdrop-filter` — so the rounded corners are clipped
  // normally without any clip-path workarounds (see SingleThumbnail).
  return (
    <div
      className={classNames(
        'group relative overflow-hidden rounded-t-md',
        showLocked ? 'aspect-[337/386]' : undefined
      )}
    >
      {showLocked ? (
        <div
          className={classNames(
            'absolute inset-0',
            isDark ? 'bg-white/10' : 'bg-black/5'
          )}
        >
          {current.thumbnailUrl && (
            <img
              src={optimizedCurrentThumbnailUrl ?? current.thumbnailUrl}
              alt=""
              aria-hidden
              draggable={false}
              // `scale-110` hides the fuzzy edge that `filter: blur` leaves
              // around the image bounds.
              className="absolute inset-0 h-full w-full scale-110 object-cover blur-[40px]"
            />
          )}
        </div>
      ) : (
        <AttachmentThumbnail
          key={safeIndex}
          mimeType={current.mimeType}
          sourceUrl={current.sourceUrl}
          thumbnailUrl={current.thumbnailUrl}
          title={title}
          variant={variant}
          mediaPlayerProps={{ autoPlay: true }}
        />
      )}

      {/* LOCK while locked. Once unlocked, video/audio autoplays via
          `mediaPlayerProps` and MediaPlayer already has its own inline
          controls, so no separate Play affordance is shown. */}
      {showLocked && (
        <LockBadge
          paymentStatus={paymentStatus}
          isPlayable={isVideoItem}
          isLocked={showLocked}
        />
      )}

      {total > 1 && (
        <React.Fragment>
          <button
            type="button"
            onClick={goPrev}
            disabled={atStart}
            aria-label="Previous item"
            className={classNames(
              'absolute left-3 top-1/2 -translate-y-1/2 flex size-7 items-center justify-center rounded-full bg-black/30 text-white transition-opacity',
              atStart ? 'opacity-40' : 'hover:bg-black/40'
            )}
          >
            <CaretLeftIcon className="size-5" weight="bold" />
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={atEnd}
            aria-label="Next item"
            className={classNames(
              'absolute right-3 top-1/2 -translate-y-1/2 flex size-7 items-center justify-center rounded-full bg-black/30 text-white transition-opacity',
              atEnd ? 'opacity-40' : 'hover:bg-black/40'
            )}
          >
            <CaretRightIcon className="size-5" weight="bold" />
          </button>

          <div className="pointer-events-none absolute bottom-10 left-1/2 flex size-8 -translate-x-1/2 items-center justify-center rounded-full bg-black/30">
            <span className="text-xs font-medium leading-none text-white">
              {safeIndex + 1}/{total}
            </span>
          </div>
        </React.Fragment>
      )}

      {topRight && (
        <div className="pointer-events-auto absolute right-3 top-3 z-10">
          {topRight}
        </div>
      )}
    </div>
  )
}

export default GalleryThumbnail
